/**
 * The published pointer on the artifact detail screen (f15.3): the state
 * badge, the per-version status pill, and the publish / unpublish
 * confirmations. Mirrors `artifact-detail.html` (pointerModal, unpublishModal).
 *
 * The server is the boundary: these render only when the detail's
 * `permissions` include `artifact:publish`, and every call is re-authorized.
 */

import { useState } from 'react';
import { CornerUpLeft, Eye, EyeOff, TriangleAlert } from 'lucide-react';

import type { ArtifactDetail, ArtifactVersion, VersionPublicationStatus } from '@/api/client';
import { generalMessage, problemFor } from '@/api/fieldErrors';
import { usePublishArtifactVersion, useUnpublishArtifact } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

const STATUS_LABELS: Record<VersionPublicationStatus, string> = {
  PUBLISHED: 'Published',
  SUPERSEDED: 'Superseded',
  NOT_PUBLISHED: 'Not published',
};

interface Publication {
  published_state: string;
  published_version?: { version_number: number } | null;
}

export function PublicationBadge({ artifact }: { artifact: Publication }) {
  if (artifact.published_state === 'PUBLISHED' && artifact.published_version) {
    return <span className="badge pill-success">Published · v{artifact.published_version.version_number}</span>;
  }
  return <span className="badge pill-neutral">Private</span>;
}

export function VersionStatusPill({ status }: { status: VersionPublicationStatus }) {
  return (
    <span className={`badge ${status === 'PUBLISHED' ? 'pill-success' : 'pill-neutral'}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

/** "Roll back to vN" when the row is older than the published version, "Publish vN" otherwise. */
export function publishActionLabel(artifact: Publication, version: Pick<ArtifactVersion, 'version_number'>): string {
  const published = artifact.published_version?.version_number;
  return published !== undefined && version.version_number < published
    ? `Roll back to v${version.version_number}`
    : `Publish v${version.version_number}`;
}

export function PublishActionButton({
  artifact,
  version,
  onClick,
}: {
  artifact: Publication;
  version: ArtifactVersion;
  onClick: () => void;
}) {
  const label = publishActionLabel(artifact, version);
  const rollback = label.startsWith('Roll back');
  return (
    <button type="button" className="btn btn-secondary btn-icon" title={label} aria-label={label} onClick={onClick}>
      {rollback ? <CornerUpLeft aria-hidden="true" /> : <Eye aria-hidden="true" />}
    </button>
  );
}

export function PublishVersionDialog({
  artifact,
  version,
  onClose,
}: {
  artifact: ArtifactDetail;
  version: ArtifactVersion;
  onClose: () => void;
}) {
  const publish = usePublishArtifactVersion(artifact);
  const n = version.version_number;
  const isPrivate = artifact.published_state !== 'PUBLISHED';
  return (
    <Modal
      title={`Make v${n} the published version?`}
      subtitle={`The external link will resolve to v${n} from now on.`}
      onClose={onClose}
      onSubmit={() => publish.mutate(version.id, { onSuccess: onClose })}
      busy={publish.isPending}
      submitLabel={isPrivate ? 'Publish' : 'Move pointer'}
      narrow
      layout="plain"
      message={generalMessage(publish.error)}
    >
      <p className="hint">
        {isPrivate
          ? 'This artifact is private. Publishing makes its external link resolve again.'
          : `The link currently serves v${artifact.published_version?.version_number}. Every version stays in the history.`}
      </p>
    </Modal>
  );
}

export function UnpublishDialog({ artifact, onClose }: { artifact: ArtifactDetail; onClose: () => void }) {
  const unpublish = useUnpublishArtifact(artifact);
  const [reason, setReason] = useState('');
  const failure = unpublish.error;
  return (
    <Modal
      title="Unpublish artifact?"
      subtitle={artifact.title}
      onClose={onClose}
      onSubmit={() => unpublish.mutate(reason.trim() || undefined, { onSuccess: onClose })}
      busy={unpublish.isPending}
      submitLabel="Unpublish"
      submitIcon={<EyeOff aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={generalMessage(failure, 'reason')}
    >
      <div className="alert alert-warning mb-4">
        <TriangleAlert aria-hidden="true" />
        <div>
          <div className="a-title">Every external route stops resolving</div>
          <div className="a-body">
            The external link returns &ldquo;not available&rdquo;. Nothing is deleted; publishing a version restores
            access.
          </div>
        </div>
      </div>
      <DialogField label="Reason" htmlFor="unpublish-reason" problem={problemFor(failure, 'reason')}>
        <textarea
          id="unpublish-reason"
          className="publication-reason"
          maxLength={500}
          value={reason}
          placeholder="Recorded on the audit entry"
          onChange={(event) => setReason(event.target.value)}
        />
      </DialogField>
    </Modal>
  );
}
